import { useParams } from "wouter";
import { useOrgSettings } from "@/lib/org-settings-context";
import { useCustomer } from "@/lib/customer-context";
import { useQuery } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import {
  ArrowLeft,
  Calendar,
  DollarSign,
  Shield,
} from "lucide-react";
import { Link } from "wouter";
import { CardVisual } from "@/components/customer/card-visual";
import { getVariantFromId, type CardVariant } from "@/lib/card-designs";

interface CardTransaction {
  id: string;
  type: string;
  status: string;
  amountCents: number;
  currency: string;
  merchantName: string | null;
  merchantCity: string | null;
  merchantCountry: string | null;
  authorizedAt: string;
  settledAt: string | null;
}

function getStatusColor(status: string): "default" | "secondary" | "destructive" | "outline" {
  const colors: Record<string, "default" | "secondary" | "destructive" | "outline"> = {
    SETTLED: "default",
    AUTHORIZED: "outline",
    PENDING: "outline",
    DECLINED: "destructive",
    REVERSED: "secondary",
  };
  return colors[status] || "secondary";
}

export default function PortalCardDetail() {
  const { formatCurrency } = useOrgSettings();
  const { customer } = useCustomer();
  const params = useParams<{ id: string }>();

  const { data: card, isLoading: cardLoading } = useQuery({
    queryKey: ["customer-card", params.id],
    queryFn: async () => {
      const response = await apiRequest("GET", `/api/customer/cards/${params.id}`);
      if (!response.ok) throw new Error("Failed to fetch card");
      return response.json();
    },
    enabled: !!params.id,
  });

  const { data: transactions = [], isLoading: txnsLoading } = useQuery<CardTransaction[]>({
    queryKey: ["customer-card-transactions", params.id],
    queryFn: async () => {
      const response = await apiRequest("GET", `/api/customer/cards/${params.id}/transactions`);
      if (!response.ok) throw new Error("Failed to fetch transactions");
      return response.json();
    },
    enabled: !!params.id,
  });

  if (cardLoading) {
    return (
      <div className="p-6 space-y-6">
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  if (!card) {
    return (
      <div className="p-6">
        <p className="text-muted-foreground">Card not found</p>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Link href="/portal/cards">
          <Button variant="ghost" size="icon">
            <ArrowLeft className="h-4 w-4" />
          </Button>
        </Link>
        <div>
          <h1 className="text-3xl font-bold">Card Details</h1>
          <p className="text-muted-foreground mt-1">
            {card.nickname || "Virtual Card"}
          </p>
        </div>
      </div>

      {/* Card Visual with flip on hover */}
      <Card className="border-2 border-primary/20">
        <CardContent className="pt-6">
          <div className="flex flex-col md:flex-row items-center gap-6">
            <CardVisual
              cardNumber={card.status === "PENDING_APPROVAL" ? "----" : (card.lastFour || "****")}
              cardHolder={`${customer?.firstName || ""} ${customer?.lastName || ""}`.trim()}
              status={card.status}
              cardType={card.cardBrand || card.cardType}
              expMonth={card.status === "PENDING_APPROVAL" ? undefined : card.expMonth}
              expYear={card.status === "PENDING_APPROVAL" ? undefined : card.expYear}
              variant={(card.cardDesign as CardVariant) || getVariantFromId(params.id || "")}
              size="md"
            />
            <div className="flex-1 space-y-3">
              <div className="flex items-center gap-2 flex-wrap">
                <h2 className="text-2xl font-bold">
                  {card.nickname || "Virtual Card"}
                </h2>
                <Badge
                  variant={card.status === "PENDING_APPROVAL" ? "outline" : getStatusColor(card.status)}
                  className={card.status === "PENDING_APPROVAL" ? "bg-amber-100 text-amber-800 border-amber-300" : ""}
                >
                  {card.status === "PENDING_APPROVAL" ? "Pending Approval" : card.status}
                </Badge>
              </div>
              <p className="text-muted-foreground text-sm">
                Hover over the card to see the back
              </p>
              <div className="flex gap-6 text-sm pt-2">
                <div>
                  <p className="text-muted-foreground">Card Number</p>
                  <p className="font-medium font-mono">
                    {card.status === "PENDING_APPROVAL" ? "---- ---- ---- ----" : `**** **** **** ${card.lastFour || "****"}`}
                  </p>
                </div>
                <div>
                  <p className="text-muted-foreground">Expires</p>
                  <p className="font-medium">
                    {card.status === "PENDING_APPROVAL" ? "--/--" : `${card.expMonth?.toString().padStart(2, "0")}/${card.expYear}`}
                  </p>
                </div>
                <div>
                  <p className="text-muted-foreground">Brand</p>
                  <p className="font-medium uppercase">{card.cardBrand || "-"}</p>
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Card Limits */}
      <div className="grid gap-4 md:grid-cols-3">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Daily Limit</CardTitle>
            <Calendar className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{formatCurrency(card.dailyLimitCents)}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Monthly Limit</CardTitle>
            <Calendar className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{formatCurrency(card.monthlyLimitCents)}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Per Transaction</CardTitle>
            <DollarSign className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{formatCurrency(card.singleTxnLimitCents)}</div>
          </CardContent>
        </Card>
      </div>

      {/* Transactions */}
      <Card>
        <CardHeader>
          <CardTitle>Transaction History</CardTitle>
          <CardDescription>All transactions made with this card</CardDescription>
        </CardHeader>
        <CardContent>
          {txnsLoading ? (
            <div className="space-y-3">
              {[...Array(5)].map((_, i) => (
                <Skeleton key={i} className="h-12 w-full" />
              ))}
            </div>
          ) : transactions.length > 0 ? (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Merchant</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead>Amount</TableHead>
                  <TableHead>Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {transactions.map((txn) => (
                  <TableRow key={txn.id}>
                    <TableCell>
                      <div>
                        <p className="font-medium">{txn.merchantName || "Unknown Merchant"}</p>
                        <p className="text-xs text-muted-foreground">
                          {txn.merchantCity && txn.merchantCountry 
                            ? `${txn.merchantCity}, ${txn.merchantCountry}`
                            : "-"}
                        </p>
                      </div>
                    </TableCell>
                    <TableCell className="text-sm">
                      {new Date(txn.authorizedAt).toLocaleDateString('en-IN', {
                        day: 'numeric',
                        month: 'short',
                        year: 'numeric'
                      })}
                    </TableCell>
                    <TableCell className="font-semibold">
                      {formatCurrency(txn.amountCents)}
                    </TableCell>
                    <TableCell>
                      <Badge variant={getStatusColor(txn.status)}>
                        {txn.status}
                      </Badge>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <div className="text-center py-12">
              <Shield className="mx-auto h-12 w-12 text-muted-foreground" />
              <h3 className="mt-4 text-lg font-semibold">No Transactions Yet</h3>
              <p className="mt-2 text-sm text-muted-foreground">
                Transactions made with this card will appear here
              </p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
